import { CustomTag } from '@/components/custom-tag/CustomTag';
import { DateFormatNow } from '@/components/date-format-now/DateFormatNow';
import usePersistFn from '@/hooks/usePersistFn';
import { useClientI18nModelStore } from '@/models/client-i18n/clientI18n';
import { useDocsDataModelStore } from '@/models/docs-data/docsData';
import { ApiFieldListShowType } from '@/shared/constant/ApiFieldList';
import type { ResourceHttpApiData } from '@/shared/constant/ResourceData';
import { Tooltip } from 'aui';
import clsx from 'clsx';
import trimEnd from 'lodash-es/trimEnd';
import React, { useMemo } from 'react';

export type ApiCustomFieldListProps = {
  onFilterKey?: (key: string) => boolean;
  className?: string;
};

export function ApiCustomFieldList(props: ApiCustomFieldListProps) {
  const { className, onFilterKey = () => true } = props;
  const apiFieldList = useDocsDataModelStore((x) => x.resourceData.extraData.apiFieldList);
  const clientI18n = useClientI18nModelStore((x) => x.clientI18n);
  const locale = useDocsDataModelStore((x) => x.docsBaseConfig.language);
  const oasExtensions = useDocsDataModelStore(
    (x) => (x.resourceData.extraData as ResourceHttpApiData['extraData']).oasExtensions || '',
  );
  const onFilterKeyFn = usePersistFn(onFilterKey);
  const filteredApiFieldList = React.useMemo(() => {
    return apiFieldList.filter((x) => !x.hidden && onFilterKeyFn(x.key));
  }, [apiFieldList, onFilterKeyFn]);

  const oasExtensionObject = useMemo(() => {
    try {
      return JSON.parse(oasExtensions);
    } catch (error) {
      return {};
    }
  }, [oasExtensions]);

  if (!filteredApiFieldList.length) {
    return null;
  }
  return (
    <div className={clsx('flex flex-wrap gap-x-5 gap-y-2 text-paragraph font-400', className)}>
      {filteredApiFieldList.map((x) => {
        const name = trimEnd(trimEnd(x.name, '：'), ':') + clientI18n('colon');

        return (
          <div className="flex flex-shrink-0 items-center" key={x.key}>
            <span className="mr-2 text-secondary">{name}</span>
            <span className="text-color">
              {x.type === ApiFieldListShowType.Link && (
                <a className="text-primary" href={x.value}>
                  {x.value}
                </a>
              )}
              {x.type === ApiFieldListShowType.Tag && (
                <span>
                  {x.value.map((y) => {
                    return <CustomTag key={y}>{y}</CustomTag>;
                  })}
                </span>
              )}
              {x.type === ApiFieldListShowType.Date && (
                <DateFormatNow value={x.value} format={x.format} locale={locale} />
              )}
              {x.type === ApiFieldListShowType.Text && <span>{x.value}</span>}
              {x.type === ApiFieldListShowType.Tooltip && (
                <Tooltip title={x.tooltip}>
                  <span>{x.value}</span>
                </Tooltip>
              )}
            </span>
          </div>
        );
      })}
      {Object.entries(oasExtensionObject).map(([key, value]) => {
        const name = trimEnd(trimEnd(key, '：'), ':') + clientI18n('colon');
        const valueString = typeof value === 'string' ? value : JSON.stringify(value);
        return (
          <div className="flex flex-shrink-0 items-center" key={key}>
            <span className="mr-2 text-secondary">{name}</span>
            <span className="text-color">
              <span>{valueString}</span>
            </span>
          </div>
        );
      })}
    </div>
  );
}
